/**
 * "Add device to job" → "From this site": the list of the site's devices the
 * office can link to a job.
 *
 * Rebuilt 2026-10-09 after Litsey Creek (#092678280, 81 devices). The old list
 * opened with every device pre-checked behind an "Add 81 devices" button — which
 * is how ~110 devices got bulk-linked to one job on 10/7 — showed the device
 * type and the ServiceTitan id instead of the serial, stopped at the API's
 * 100-row page cap, and dropped devices already on the job without a word.
 *
 * Now: nothing is checked on open, the serial leads every row, a search box
 * narrows by serial / make / model / size / location, devices already on the
 * job stay visible but greyed with "on this job", and picking more than
 * {@link CONFIRM_OVER} asks "Add N devices to this job?" inside the dialog.
 */
import { useMemo, useState } from "react";
import { AlertTriangle, Search, X } from "lucide-react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface SiteAssetOption {
    id: string;
    site_id: string;
    name: string;
    external_key: string | null;
    asset_kind: string | null;
    notes: string | null;
    serial_number: string | null;
    assembly_type: string | null;
    parent_asset_id: string | null;
    size_label?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    location_description?: string | null;
    /** Non-null renders the amber "Verify on site" chip (authority-list reconcile). */
    needs_verification_reason: string | null;
    /** DCDA/RPDA bypass leg, nested by the site-assets API (children are hidden from the top-level list). */
    bypass?: {
        id: string;
        size_label: string | null;
        manufacturer: string | null;
        model: string | null;
        serial_number: string | null;
    } | null;
}

/** Picking more than this many devices asks for an in-dialog confirmation. */
export const CONFIRM_OVER = 10;

/** The server caps one page of `/api/sites/:id/assets` at 100 rows. */
const PAGE = 100;
/** Hard stop so a runaway total can't loop forever. */
const MAX_PAGES = 50;

/**
 * Every active top-level device on the site, across as many pages as it takes.
 * `?limit=200` was silently clamped to 100 by the server, so a site with more
 * than 100 devices lost everything past the 100th name — the "it won't go down
 * past x" report.
 */
export async function fetchAllSiteAssets(siteId: string): Promise<{ assets: SiteAssetOption[] }> {
    const all: SiteAssetOption[] = [];
    for (let page = 0; page < MAX_PAGES; page++) {
        const resp = await api.get<{ assets: SiteAssetOption[]; total?: number }>(
            `/api/sites/${siteId}/assets?limit=${PAGE}&offset=${page * PAGE}`,
        );
        const rows = resp.assets ?? [];
        all.push(...rows);
        const total = typeof resp.total === "number" ? resp.total : null;
        if (rows.length < PAGE || (total != null && all.length >= total)) break;
    }
    return { assets: all };
}

/** Serial as people type it: no case, spaces, dashes, dots, or "SN"/"S/N"/"#" prefix. */
export function normalizeSerial(raw: string | null | undefined): string {
    if (!raw) return "";
    return raw
        .toLowerCase()
        .trim()
        .replace(/^(s\s*\/\s*n|sn|serial)\s*[#:.-]?\s*/, "")
        .replace(/[^a-z0-9]/g, "");
}

/** Does this device match the search box? Serial match ignores punctuation; other words AND across fields. */
export function matchesDeviceSearch(a: SiteAssetOption, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const nq = normalizeSerial(q);
    const serials = [a.serial_number, a.bypass?.serial_number].map(normalizeSerial).filter(Boolean);
    if (nq && serials.some((s) => s.includes(nq))) return true;
    const hay = [
        a.serial_number,
        a.bypass?.serial_number,
        a.manufacturer,
        a.model,
        a.size_label,
        a.location_description,
        a.name,
        a.assembly_type,
        a.external_key,
        a.bypass?.manufacturer,
        a.bypass?.model,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
    return q
        .split(/\s+/)
        .every(
            (tok) =>
                hay.includes(tok) || (normalizeSerial(tok) && serials.some((s) => s.includes(normalizeSerial(tok)))),
        );
}

const TYPE_LABEL: Record<string, string> = {
    rpz: "RPZ",
    dc: "DC",
    dcda: "DCDA",
    rpda: "RPDA",
    pvb: "PVB",
    svb: "SVB",
};

function describe(a: SiteAssetOption): string {
    const type = a.assembly_type ? (TYPE_LABEL[a.assembly_type] ?? a.assembly_type.toUpperCase()) : null;
    const makeModel = [a.manufacturer, a.model].filter(Boolean).join(" ");
    return [a.size_label, type, makeModel].filter(Boolean).join(" · ");
}

export function SiteDeviceList({
    assets,
    linkedAssetIds,
    pending,
    onAdd,
}: {
    /** The site's devices for this trade — including ones already on the job (shown greyed). */
    assets: SiteAssetOption[];
    linkedAssetIds: Set<string>;
    pending: boolean;
    onAdd: (ids: string[]) => void;
}) {
    // Nothing pre-checked. Ever. (10/7: a pre-checked "Add 81 devices" is how
    // a whole site landed on one job.)
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    const [query, setQuery] = useState("");
    const [confirming, setConfirming] = useState(false);

    const bypassOf = (a: SiteAssetOption) => (a.bypass && !linkedAssetIds.has(a.bypass.id) ? a.bypass : null);

    // Addable devices first, then the ones already on the job; serial order within each.
    const ordered = useMemo(() => {
        const key = (a: SiteAssetOption) => (a.serial_number ?? "").trim() || `￿${a.name ?? ""}`;
        return [...assets].sort((x, y) => {
            const xo = linkedAssetIds.has(x.id) ? 1 : 0;
            const yo = linkedAssetIds.has(y.id) ? 1 : 0;
            return xo - yo || key(x).localeCompare(key(y), undefined, { numeric: true });
        });
    }, [assets, linkedAssetIds]);
    const visible = ordered.filter((a) => matchesDeviceSearch(a, query));
    const addableCount = assets.filter((a) => !linkedAssetIds.has(a.id)).length;
    const onJobCount = assets.length - addableCount;

    // "Check all shown" works on the filtered list, so search-then-check-all is
    // the bulk path — deliberate, never the default.
    const visibleSelectable = visible.flatMap((a) => {
        if (linkedAssetIds.has(a.id)) {
            const child = bypassOf(a);
            return child ? [child.id] : [];
        }
        const child = bypassOf(a);
        return child ? [a.id, child.id] : [a.id];
    });
    const allVisibleChecked = visibleSelectable.length > 0 && visibleSelectable.every((id) => selected.has(id));

    const toggle = (ids: string[], on: boolean) => {
        setConfirming(false);
        setSelected((prev) => {
            const next = new Set(prev);
            for (const id of ids) {
                if (on) next.add(id);
                else next.delete(id);
            }
            return next;
        });
    };

    const count = selected.size;
    const label = `Add ${count} device${count === 1 ? "" : "s"}`;
    const submit = () => {
        if (count > CONFIRM_OVER && !confirming) {
            setConfirming(true);
            return;
        }
        onAdd([...selected]);
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 space-y-2 border-b p-3">
                <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        autoFocus
                        aria-label="Search devices"
                        placeholder="Search serial, make, model, size, location"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        className="pl-8 pr-8"
                    />
                    {query && (
                        <button
                            type="button"
                            aria-label="Clear search"
                            onClick={() => setQuery("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                        {query ? `${visible.length} of ${assets.length} shown` : `${assets.length} on this site`}
                        {onJobCount > 0 ? ` · ${onJobCount} already on this job` : ""}
                    </span>
                    {visibleSelectable.length > 0 && (
                        <button
                            type="button"
                            className="text-primary hover:underline"
                            onClick={() => toggle(visibleSelectable, !allVisibleChecked)}
                        >
                            {allVisibleChecked
                                ? "Uncheck shown"
                                : `Check ${query ? "shown" : "all"} (${visibleSelectable.length})`}
                        </button>
                    )}
                </div>
            </div>

            <ul data-testid="site-device-list" className="min-h-0 flex-1 divide-y overflow-y-auto overscroll-contain">
                {visible.length === 0 && (
                    <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                        No device matches “{query}”.
                    </li>
                )}
                {visible.map((a) => {
                    const onJob = linkedAssetIds.has(a.id);
                    const checked = !onJob && selected.has(a.id);
                    const child = bypassOf(a);
                    const childChecked = child ? selected.has(child.id) : false;
                    const desc = describe(a);
                    const where = a.location_description?.trim() || a.name;
                    return (
                        <li key={a.id} data-testid="site-device-row" data-on-job={onJob ? "true" : undefined}>
                            <label
                                className={cn(
                                    "flex items-start gap-3 px-3 py-2.5",
                                    onJob ? "cursor-not-allowed bg-muted/60" : "cursor-pointer hover:bg-muted/50",
                                )}
                            >
                                <input
                                    type="checkbox"
                                    aria-label={`Select ${a.serial_number ? `serial ${a.serial_number}` : a.name}`}
                                    checked={onJob || checked}
                                    disabled={onJob}
                                    onChange={() => {
                                        const ids = child ? [a.id, child.id] : [a.id];
                                        toggle(ids, !checked);
                                    }}
                                    className="mt-1 h-4 w-4 shrink-0 rounded accent-primary"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                        {a.serial_number ? (
                                            <span
                                                className={cn(
                                                    "font-mono text-sm font-semibold tracking-wide",
                                                    onJob ? "text-muted-foreground" : "text-foreground",
                                                )}
                                            >
                                                S/N {a.serial_number}
                                            </span>
                                        ) : (
                                            <span className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                                                No serial on file
                                            </span>
                                        )}
                                        {onJob && (
                                            <Badge variant="outline" className="text-[10px] text-muted-foreground">
                                                on this job
                                            </Badge>
                                        )}
                                        {a.needs_verification_reason && (
                                            <Badge
                                                variant="outline"
                                                className="inline-flex items-center gap-1 border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-700 dark:text-amber-400"
                                                title={a.needs_verification_reason}
                                            >
                                                <AlertTriangle className="h-3 w-3" /> Verify on site
                                            </Badge>
                                        )}
                                    </div>
                                    {desc && (
                                        <div
                                            className={cn(
                                                "text-sm",
                                                onJob ? "text-muted-foreground" : "text-foreground",
                                            )}
                                        >
                                            {desc}
                                        </div>
                                    )}
                                    <div className="flex items-baseline gap-2 text-xs text-muted-foreground">
                                        <span className="min-w-0 truncate">{where}</span>
                                        {a.external_key && (
                                            <span className="shrink-0 text-[10px]">ST {a.external_key}</span>
                                        )}
                                    </div>
                                </div>
                            </label>
                            {child && (
                                <label className="ml-5 flex cursor-pointer items-center gap-3 border-l-2 border-l-primary/20 py-1.5 pl-10 pr-3 hover:bg-muted/50">
                                    <input
                                        type="checkbox"
                                        aria-label="Include its bypass"
                                        checked={childChecked}
                                        onChange={() => toggle([child.id], !childChecked)}
                                        className="h-3.5 w-3.5 shrink-0 rounded accent-primary"
                                    />
                                    <div className="min-w-0 text-xs">
                                        <span className="font-medium">Include its bypass</span>
                                        <span className="text-muted-foreground">
                                            {" — "}
                                            {child.serial_number ? `S/N ${child.serial_number} · ` : ""}
                                            {[child.size_label, child.manufacturer, child.model]
                                                .filter(Boolean)
                                                .join(" ") || "bypass leg"}
                                        </span>
                                    </div>
                                </label>
                            )}
                        </li>
                    );
                })}
            </ul>

            <div className="shrink-0 border-t p-3">
                {confirming ? (
                    <div
                        role="alertdialog"
                        aria-label="Confirm adding devices"
                        className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2"
                    >
                        <span className="text-sm font-semibold text-foreground">Add {count} devices to this job?</span>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
                                Go back
                            </Button>
                            <Button size="sm" disabled={pending} onClick={submit}>
                                {pending ? "Adding…" : `Yes, add ${count}`}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">
                            {count === 0 ? "Tick the devices this job is for." : `${count} selected`}
                        </span>
                        <div className="flex gap-2">
                            {count > 0 && (
                                <Button size="sm" variant="ghost" onClick={() => toggle([...selected], false)}>
                                    Clear
                                </Button>
                            )}
                            <Button size="sm" disabled={count === 0 || pending} onClick={submit}>
                                {pending ? "Adding…" : label}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
